import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Phone, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { isFirebaseConfigured, isFirebaseTestMode, sendPhoneOtp, resetRecaptchaVerifier } from "@/lib/firebase";
import { useAuth } from "@/lib/auth";

const RECAPTCHA_CONTAINER_ID = "psg-recaptcha-container";

/**
 * <PhoneOtpLogin /> — Two-step phone OTP UI for spa owners.
 *
 *   Step 1: 10-digit Indian phone → sendPhoneOtp() (uses invisible reCAPTCHA)
 *   Step 2: 6-digit OTP            → confirmationResult.confirm(otp)
 *                                  → POST Firebase ID token to /api/auth/phone/verify
 *
 * Modes:
 *   mode="login"     (default) — backend creates/logs-in the user, this
 *                                  component handles redirect to /dashboard.
 *   mode="register"  — backend validates the token and returns a short-lived
 *                       phone_verification_token. The component calls
 *                       `onVerified({phone, verification_token})` and the
 *                       parent wizard advances to the next step.
 *
 * Props:
 *   mode                  "login" | "register"            (default "login")
 *   onVerified            ({phone, verification_token}) => void   (register mode)
 *   onEmailFallbackClick  () => void   — called when the user clicks "Sign in with email"
 *   hideNameField         boolean — register wizard collects the name later
 */
export default function PhoneOtpLogin({ mode = "login", onVerified, onEmailFallbackClick, hideNameField = false }) {
  const { setUser } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState("phone");     // 'phone' | 'otp'
  const [phone, setPhone] = useState("");        // 10 digits, no country code
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const configured = isFirebaseConfigured();

  // Clean up the reCAPTCHA verifier whenever this component unmounts so a
  // re-mount can attach a fresh one (Firebase doesn't allow two on one page).
  useEffect(() => () => resetRecaptchaVerifier(), []);

  // 30-second resend countdown.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sanitizedPhone = phone.replace(/\D/g, "").slice(0, 10);
  const canSend = configured && sanitizedPhone.length === 10 && !sending;

  const sendOtp = useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const e164 = `+91${sanitizedPhone}`;
      const result = await sendPhoneOtp(e164, RECAPTCHA_CONTAINER_ID);
      setConfirmation(result);
      setStep("otp");
      setResendIn(30);
      toast.success("OTP sent to your phone.");
    } catch (err) {
      // Firebase surfaces specific codes — translate the common ones.
      const code = err?.code || "";
      let msg = "Could not send OTP. Please try again.";
      if (code === "auth/invalid-phone-number") msg = "Phone number is invalid.";
      else if (code === "auth/too-many-requests") msg = "Too many attempts. Please wait a few minutes.";
      else if (code === "auth/captcha-check-failed") msg = "reCAPTCHA check failed. Please retry.";
      else if (code === "auth/quota-exceeded") msg = "Daily OTP quota exceeded. Try again tomorrow.";
      else if (code === "auth/billing-not-enabled") msg = "Live phone auth requires Firebase Blaze plan. Try a test number.";
      else if (code === "auth/argument-error" || code === "auth/internal-error") {
        // Most common cause: this domain isn't in Firebase Authorized Domains.
        // Surface a helpful, actionable message instead of the cryptic SDK one.
        msg = "Phone auth isn't authorized for this domain. Please add this site to Firebase → Authentication → Settings → Authorized domains.";
      }
      else if (err?.message) msg = err.message;
      // Aid debugging — full error in console only.
      // eslint-disable-next-line no-console
      console.error("[PhoneOtp] sendOtp failed", { code, message: err?.message, err });
      toast.error(msg);
      resetRecaptchaVerifier();
    } finally {
      setSending(false);
    }
  }, [canSend, sanitizedPhone]);

  const verifyOtp = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!confirmation) return toast.error("Please request an OTP first.");
    if (otp.length < 6) return toast.error("Enter the 6-digit OTP.");
    setVerifying(true);
    try {
      const credential = await confirmation.confirm(otp);
      const idToken = await credential.user.getIdToken();
      const { default: api } = await import("@/lib/api");

      if (mode === "register") {
        // Wizard mode — backend only validates + returns a verification token.
        // Account creation happens later in the wizard's final step.
        const res = await api.post("/auth/phone/verify", {
          firebase_id_token: idToken,
          mode: "register",
        });
        toast.success("Phone verified!");
        onVerified?.({
          phone: res.data.phone,
          verification_token: res.data.verification_token,
        });
        return;
      }

      // Default login mode — exchange the token for a session cookie.
      const res = await api.post("/auth/phone/verify", {
        firebase_id_token: idToken,
        name: name || "Spa Owner",
      });
      localStorage.setItem("psg_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success("Welcome to Pune Spa Guide!");
      nav("/dashboard");
    } catch (err) {
      const code = err?.code || "";
      let msg = err?.response?.data?.detail || "Invalid OTP. Please try again.";
      if (code === "auth/invalid-verification-code") msg = "That OTP is incorrect.";
      else if (code === "auth/code-expired") msg = "That OTP has expired. Please request a new one.";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  }, [confirmation, otp, name, setUser, nav, mode, onVerified]);

  const goBack = useCallback(() => {
    setStep("phone");
    setOtp("");
    setConfirmation(null);
    resetRecaptchaVerifier();
  }, []);

  if (!configured) {
    return (
      <div className="rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] p-4 text-sm text-[#7A5A22]" data-testid="phone-login-misconfigured">
        Phone login is not configured. Please use the email option below.
        {onEmailFallbackClick && (
          <button onClick={onEmailFallbackClick} className="block text-[#1F4E3D] underline mt-2" data-testid="email-fallback-link">
            Sign in with email →
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="phone-otp-login">
      {isFirebaseTestMode() && (
        <div className="rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] px-3 py-2 text-[11px] text-[#7A5A22]" data-testid="firebase-test-mode-banner">
          <span className="font-semibold">Test mode active</span> — only Firebase-whitelisted phone numbers
          (e.g. <code className="font-mono">+91 9930834774</code> / OTP <code className="font-mono">123456</code>) will work until reCAPTCHA Enterprise is configured.
        </div>
      )}
      {step === "phone" ? (
        <>
          <div>
            <Label className="text-xs">Mobile number</Label>
            <div className="flex gap-2 mt-1.5">
              <div className="flex items-center px-3 rounded-md border border-[#E7DDCF] bg-[#F5EFE6] text-sm text-[#4B5563]">+91</div>
              <Input
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={sanitizedPhone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1"
                data-testid="phone-input"
                autoComplete="tel-national"
              />
            </div>
          </div>
          {!hideNameField && (
            <div>
              <Label className="text-xs">Your name (optional)</Label>
              <Input
                placeholder="So we can address you correctly"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="phone-name-input"
                autoComplete="name"
              />
            </div>
          )}
          <Button
            onClick={sendOtp}
            disabled={!canSend}
            className="w-full h-11 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
            data-testid="send-otp-button"
          >
            {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Phone size={16}/> Send OTP</>}
          </Button>
        </>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-4">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-xs text-[#7A5A22] hover:text-[#1F4E3D]"
            data-testid="phone-back-button"
          >
            <ArrowLeft size={12}/> Change number
          </button>
          <div>
            <Label className="text-xs">Enter the 6-digit OTP sent to +91{sanitizedPhone}</Label>
            <Input
              inputMode="numeric"
              placeholder="123456"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              className="text-center tracking-[0.5em] text-xl font-medium"
              data-testid="otp-input"
              autoFocus
              autoComplete="one-time-code"
            />
          </div>
          <Button
            type="submit"
            disabled={verifying || otp.length < 6}
            className="w-full h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] gap-2"
            data-testid="verify-otp-button"
          >
            {verifying ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : "Verify & Sign in"}
          </Button>
          <div className="text-xs text-center text-[#4B5563]">
            {resendIn > 0 ? (
              <span data-testid="resend-countdown">Resend OTP in {resendIn}s</span>
            ) : (
              <button
                type="button"
                onClick={sendOtp}
                className="text-[#1F4E3D] hover:underline"
                data-testid="resend-otp-button"
              >
                Resend OTP
              </button>
            )}
          </div>
        </form>
      )}

      {/* Invisible reCAPTCHA — Firebase mounts the bot challenge here. */}
      <div id={RECAPTCHA_CONTAINER_ID} data-testid="recaptcha-container" />
    </div>
  );
}
